import { useState, useEffect, useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import StaffMessagesThread from '../../components/StaffMessagesThread';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { MessageSquare, Search, User, Network } from 'lucide-react';
import { formatUK } from '../../lib/ukTime';
import { deptBySlug, deptDisplay } from '../../lib/departments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Primary Admin "enters" a department and messages staff with the DEPARTMENT
// identity. Same shared conversations as the real department members.
export default function AdminDepartmentPage() {
  const { slug } = useParams();
  const dept = deptBySlug(slug);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchOverview = useCallback(async () => {
    if (!dept) return;
    try {
      const res = await axios.get(`${API}/admin/staff-messages/overview`, { params: { department: dept.name } });
      setStaff(res.data.staff || []);
    } catch { /* empty state covers it */ } finally {
      setLoading(false);
    }
  }, [dept]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useAutoRefresh(fetchOverview, { match: ['/messages'] });

  if (!dept) return <Navigate to="/admin" replace />;
  const label = deptDisplay(dept.name);

  const filtered = staff.filter((s) =>
    !search.trim() || (s.name || '').toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <AdminLayout>
      <div className="p-3 sm:p-6 space-y-5 min-h-screen" data-testid="admin-department-page">
        <div className="border-b border-border pb-4">
          <h1 className="text-xl sm:text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <Network className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" strokeWidth={1.5} />
            {label}
          </h1>
          <div className="mt-2 inline-flex items-center gap-2 bg-cyan-400/10 border border-cyan-400/30 rounded-sm px-3 py-1.5" data-testid="dept-context-banner">
            <MessageSquare className="w-3.5 h-3.5 text-cyan-400" />
            <p className="text-xs text-cyan-300">
              You are working inside <b>{label}</b> — messages you send here reach employees as <b>{label}</b>, not under your name.
            </p>
          </div>
        </div>

        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search staff by name..."
            className="pl-9 rounded-sm"
            data-testid="dept-messages-search-input"
          />
        </div>

        {loading ? (
          <div className="py-10 text-center animate-pulse text-muted-foreground">Loading...</div>
        ) : (
          <div className="divide-y divide-border border border-border rounded-sm bg-card" data-testid="dept-messages-staff-list">
            {filtered.length === 0 && (
              <p className="p-6 text-sm text-muted-foreground text-center">No staff found.</p>
            )}
            {filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex items-center gap-3"
                data-testid={`dept-messages-row-${s.id}`}
              >
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm truncate ${s.unread ? 'font-bold text-foreground' : ''}`}>{s.name}</span>
                    {s.unread > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-primary-foreground bg-primary rounded-full px-2 py-0.5">
                        <MessageSquare className="w-3 h-3" /> {s.unread}
                      </span>
                    )}
                  </div>
                  <p className={`text-xs truncate mt-0.5 ${s.unread ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {s.last_at ? s.last_body : 'No messages yet'}
                  </p>
                </div>
                {s.last_at && (
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                    {formatUK(s.last_at, 'd MMM HH:mm')}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); fetchOverview(); } }}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="dept-thread-sheet">
          <SheetHeader>
            <SheetTitle className="font-barlow tracking-widest uppercase flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-cyan-400" /> {selected?.name}
            </SheetTitle>
          </SheetHeader>
          <p className="mt-2 text-[11px] text-cyan-300 bg-cyan-400/10 border border-cyan-400/30 rounded-sm px-2.5 py-1.5">
            Sending as <b>{label}</b> — the employee will not see your name.
          </p>
          <div className="mt-3">
            {selected && <StaffMessagesThread viewer="admin" employeeId={selected.id} department={dept.name} />}
          </div>
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
}
